/**
 * What each PDLC task produces (#1108): a file, a link, or a record ATLAS
 * already holds. The split is the one written on #1108 (2026-09-25), read from
 * the Pro template; Jose and the PMs are asked to confirm it.
 *
 * Every task still takes uploads (decided 2026-09-25). This table only drives
 * the Deliverables card, the upload area's hint text, and the link and record slots.
 */
import { PDLC_TEMPLATE_RENAMES, normalizeTaskName } from './pdlc-board.js';
const file = (task, fileTypes) => ({ task, produces: 'file', ...(fileTypes && { fileTypes }) });
const handoff = (task, fileTypes) => ({ ...file(task, fileTypes), handoff: true });
const link = (task) => ({ task, produces: 'link' });
const record = (task, field) => ({ task, produces: 'record', field });
export const PDLC_PRODUCES = [
    // File, MKTG
    file('Create Product Logo / Wordmark', 'AI, EPS, SVG or PNG'),
    file('Create 3D Product Renders', 'PNG, JPG or TIFF'),
    file('Create Application Renders (If Required)', 'PNG, JPG or TIFF'),
    file('Create Retail Color Box (Retail Only)', 'PDF or AI'),
    file('Finalize Product Genesis Document', 'DOCX or PDF'),
    file('Create Commercialization Plan (A Launch)', 'DOCX, PPTX or PDF'),
    file('Build Preliminary Product Page & Print PDF (Cutsheet)', 'PDF'),
    file('Upload Preliminary DAM Assets'),
    file('Complete FR Translations', 'DOCX or PDF'),
    file('Complete DE Translations', 'DOCX or PDF'),
    file('Complete ES Translations', 'DOCX or PDF'),
    file('Finalize DAM Assets'),
    // File, PD
    file('Complete Product Proposal Form', 'PDF or XLSX'),
    file('Complete Competitive Matrix', 'XLSX or PDF'),
    file('Complete PPS', 'XLSX or PDF'),
    file('Define Fixture DNA', 'PDF or XLSX'),
    file('Create Product Walkthrough Video', 'MP4 or MOV'),
    file('OUTERBOX DESIGN', 'PDF or AI'),
    file('Create QRG', 'PDF'),
    file('Define PLS Testing Plan', 'DOCX, XLSX or PDF'),
    file('Complete PLS Testing', 'XLSX or PDF'),
    file('Complete BOM List', 'XLSX'),
    file('Finalize GDTF File', '.gdtf'),
    file('Publish Service Manual, Parts etc.', 'PDF'),
    // File, TW
    file('Create Product QR Code', 'PNG, SVG or PDF'),
    file('Generate Preliminary Cutsheet', 'PDF'),
    file('Upload IES Files', '.ies'),
    file('Upload Photometrics Report', 'PDF'),
    file('Create DMX Chart', 'PDF or XLSX'),
    file('Upload Approved Certifications & Reports', 'PDF'),
    file('Upload User Manual', 'PDF'),
    // File handoffs
    handoff('Receive Initial Supplier Documentation'),
    handoff('Receive CAD / STP files', 'STP or STEP'),
    handoff('Release CAD/STP Files for Rendering', 'STP or STEP'),
    handoff('Prepare CAD Files for Tech Writing', 'STP, STEP or DWG'),
    handoff('Receive Initial GDTF File', '.gdtf'),
    handoff('Release QRG & OEM to Factory', 'PDF'),
    handoff('Release Initial Firmware to TW'),
    // Link or place
    link('Create (PRIVATE) Github Repository'),
    link('Publish Github Repository'),
    link('Create Product Page URL'),
    link('Upload Product Page Translations'),
    link('Distribute Final GDTF File'),
    link('Create LMS Project'),
    link('Publish LMS Training'),
    link('Publish Product Page (GO LIVE)'),
    record('Upload Final Specs and Build Info', 'the product’s Specs tab'),
    // Text or a decision
    record('Finalize Product Name', 'the Naming card'),
    record('Create Product Description', 'the product’s description'),
    record('Prepare Internal Company-Wide Go-Live Email', 'a bulletin'),
    record('Prepare Internal Campaign Alert', 'a bulletin'),
];
const BY_KEY = new Map(PDLC_PRODUCES.map((e) => [normalizeTaskName(e.task), e]));
/** Older templates' names → the current name, the same map the PDLC Board uses. */
const RENAMED = new Map(Object.entries(PDLC_TEMPLATE_RENAMES).map(([from, to]) => [normalizeTaskName(from), normalizeTaskName(to)]));
/**
 * The entry for a task by name, or undefined. Matched loosely ("Upload IES
 * Files (.ies)" too), and an older template's name counts as its current one.
 */
export function pdlcProducesFor(taskName) {
    const raw = normalizeTaskName(taskName);
    const key = RENAMED.get(raw) ?? raw;
    const exact = BY_KEY.get(key);
    if (exact)
        return exact;
    // Template names sometimes carry a trailing note, e.g. "Upload IES Files (`.ies`)".
    for (const [k, e] of BY_KEY)
        if (key.startsWith(k))
            return e;
    return undefined;
}
//# sourceMappingURL=pdlc-produces.js.map